import { Popper, PopperProps } from '@mui/material';
import * as React from 'react';
import * as S from './popover-menu.styles';

export interface IPopoverMenuProps {
    align?: 'left' | 'right';
    // 'auto' (default) opens below and flips above when the menu doesn't fit;
    // 'top' / 'bottom' pin the side.
    verticalAlign?: 'top' | 'bottom' | 'auto';
    open?: boolean;
    activator: React.RefObject<HTMLElement>;
    children?: React.ReactNode;
}

type Modifiers = NonNullable<PopperProps['modifiers']>;
type Placement = NonNullable<PopperProps['placement']>;

// Distance between the activator and the menu.
const GAP = 8;
// Minimum distance kept between the menu and the viewport edge.
const VIEWPORT_PADDING = 8;
// Below this the menu is unusable; let it overflow rather than shrink further.
const MIN_HEIGHT = 120;
// Fixed so a menu near the bottom can never grow the page and spawn a
// document scrollbar. Module-level: MUI rebuilds the popper instance
// whenever this object's identity changes.
const POPPER_OPTIONS: PopperProps['popperOptions'] = { strategy: 'fixed' };

// Caps the menu at the space left on the side it opened on, so a long menu
// scrolls inside itself instead of hanging past the viewport. Runs after
// `flip`, so it sees the final placement.
const maxHeightModifier: Modifiers[number] = {
    name: 'maxHeight',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        const rect = (state.elements.reference as Element).getBoundingClientRect();
        const opensAbove = state.placement.startsWith('top');
        const space = opensAbove
            ? rect.top - GAP - VIEWPORT_PADDING
            : window.innerHeight - rect.bottom - GAP - VIEWPORT_PADDING;
        state.styles.popper.maxHeight = `${Math.max(space, MIN_HEIGHT)}px`;
    },
};

const placementFor = (align: 'left' | 'right', side: 'top' | 'bottom'): Placement =>
    `${side}-${align === 'right' ? 'end' : 'start'}`;

export const PopoverMenu: React.FC<IPopoverMenuProps> = ({
    align = 'left',
    verticalAlign = 'auto',
    open,
    activator,
    children,
}) => {
    const placement = placementFor(align, verticalAlign === 'top' ? 'top' : 'bottom');

    const modifiers = React.useMemo<Modifiers>(() => {
        // 'auto' may flip to the other side; a pinned side never does.
        // Left-aligned menus may switch to right-aligned when they'd run off
        // the right edge; right-aligned menus stay put and get shifted instead.
        const sides: ('top' | 'bottom')[] =
            verticalAlign === 'auto' ? ['bottom', 'top'] : [verticalAlign === 'top' ? 'top' : 'bottom'];
        const aligns: ('left' | 'right')[] = align === 'left' ? ['left', 'right'] : ['right'];
        const fallbackPlacements = aligns
            .flatMap(a => sides.map(side => placementFor(a, side)))
            .filter(p => p !== placement);

        return [
            { name: 'offset', options: { offset: [0, GAP] } },
            {
                name: 'flip',
                enabled: fallbackPlacements.length > 0,
                options: { fallbackPlacements, padding: VIEWPORT_PADDING },
            },
            { name: 'preventOverflow', options: { padding: VIEWPORT_PADDING } },
            maxHeightModifier,
        ];
    }, [align, verticalAlign, placement]);

    // Resolved lazily so the activator ref is attached by the time the
    // popper instance is created, even when the menu mounts open.
    const getAnchor = React.useCallback(() => activator.current as HTMLElement, [activator]);

    return (
        <Popper
            open={!!open}
            anchorEl={getAnchor}
            placement={placement}
            modifiers={modifiers}
            popperOptions={POPPER_OPTIONS}
            // MUI defaults the root to role="tooltip"; this is a menu container.
            role={undefined}
            style={{ zIndex: S.MENU_Z_INDEX }}>
            <S.Menu>{children}</S.Menu>
        </Popper>
    );
};
